import preventDefault
  from '@mailobj-browser/front/js/events/hooks/preventDefault.js'
import stopImmediatePropagation
  from '@mailobj-browser/front/js/events/hooks/stopImmediatePropagation.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import capture from '@mailobj-browser/front/js/events/options/capture.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import blur from '@mailobj-browser/front/js/events/types/blur.js'
import change from '@mailobj-browser/front/js/events/types/change.js'
import input from '@mailobj-browser/front/js/events/types/input.js'
import keyDown from '@mailobj-browser/front/js/events/types/keyDown.js'
import mouseOut from '@mailobj-browser/front/js/events/types/mouseOut.js'
import mouseOver from '@mailobj-browser/front/js/events/types/mouseOver.js'
import mouseUp from '@mailobj-browser/front/js/events/types/mouseUp.js'
import touchEnd from '@mailobj-browser/front/js/events/types/touchEnd.js'
import { scroll } from '@mailobj-browser/front/js/events/types/types.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import closest from '@mailobj-browser/front/js/selectors/closest.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import replaceChildren from '@mailobj-browser/front/js/tree/replaceChildren.js'
import removed from '@mailobj-browser/front/js/tree/removed.js'
import mutations from '@mailobj-browser/front/js/tree/mutations.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import resolvable from '@mailobj-browser/front/js/utils/resolvable.js'
import rect from '#utilities/js/ea/rect.js'
import * as fixed from '#utilities/js/fixed/fixed.js'
import * as utilities from '#utilities/js/styles.js'
import { render } from '#utilities/js/template/template.js'
import { elements, modifiers } from '../styles.js'
import * as sharedClasses from '#utilities/js/ea/sharedClasses.js'

const lists = new WeakSet()
const current = elements.current
const options = object(null, { childList: true })
let state = {}

const keys = object(null, {
  Tab (
    field
  ) {
    const { radios } = state

    if (!radios.includes(field)) {
      return
    }

    resolve(field)
  },
  Enter (
    field,
    event
  ) {
    const { radios, target } = state

    if (!radios.includes(field)) {
      return
    }

    preventDefault(event)
    stopImmediatePropagation(event)
    resolve(field)
    target.focus()
  },
  ' ' () {
    const { target } = state

    target.focus()
  },
  ArrowUp (
    field,
    event
  ) {
    const { index } = state

    if (event) {
      preventDefault(event)
    }

    move(index - 1)
  },
  ArrowDown (
    field,
    event
  ) {
    const { index } = state

    if (event) {
      preventDefault(event)
    }

    move(index + 1)
  }
})

const onBlur = object(listener, {
  type: blur,
  passive,
  task: (
    input,
    event
  ) => {
    const { fields, skip } = state

    if (skip > 0) {
      state.skip -= 1

      return
    }

    const { relatedTarget } = event

    if (!fields.includes(relatedTarget)) {
      reject()
    }
  }
})

const onChange = object(listener, {
  type: change,
  passive,
  task: (
    radio
  ) => {
    resolve(radio)
  }
})

const onKeyDown = object(listener, {
  type: keyDown,
  capture,
  task: (
    input,
    event
  ) => {
    const { key } = event

    state.skip = 0

    if (key in keys) {
      keys[key](input, event)

      return
    }

    const { target } = state

    target.focus()
  }
})

const onInput = object(listener, {
  type: input,
  passive,
  task: async (
    input
  ) => {
    const { value } = input

    if (!value) {
      hide()

      return
    }

    await suggest()
  }
})

const onMouseUp = object(listener, {
  type: mouseUp,
  task: async (
    element,
    event
  ) => {
    const { begin = -1, target } = state

    if (element !== target) {
      const input = one('input', element) ?? element

      preventDefault(event)
      resolve(input)
      target.focus()

      return
    }

    const { value } = target

    if (begin === -1 && value) {
      empty()
      await suggest()
      move(0, false)
    }
  }
})

const onMouseOver = object(listener, {
  type: mouseOver,
  passive,
  task: async (
    label
  ) => {
    const { begin = -1, fields } = state
    const radio = one('input', label)

    if (begin === -1) {
      empty()

      try {
        await suggest()
      } catch {}
    }

    state.skip += 1
    move(fields.indexOf(radio), false)
  }
})

const onMouseOut = object(listener, {
  type: mouseOut,
  passive,
  task: () => {
    state.skip -= 1
  }
})

const onTouchEnd = object(listener, {
  ...onMouseUp,
  type: touchEnd
})

const onScroll = object(listener, {
  type: scroll,
  passive,
  capture,
  task: (
    document,
    { target }
  ) => {
    const { list } = state

    if (!list.contains(target) && target.contains(list)) {
      reject()
    }
  }
})

const hide = () => {
  const { list, target } = state
  const { ownerDocument } = list
  const lightboxes = closest(`.${utilities.elements.aside_lightboxes}`, list)
  const begin = -1
  const index = 0
  const fields = array([target])
  const radios = array([])

  update({ begin, fields, index, radios })
  onScroll.forget(ownerDocument)
  empty()
  list.classList.remove(modifiers.list.open)

  if (!lightboxes) {
    sharedClasses.remove(list, utilities.modifiers.overflow.hidden)
  }
}

const empty = () => {
  const { list, target } = state
  const fields = array([target])

  replaceChildren(list)

  update({ fields })
}

const reject = () => {
  const { reject } = state

  hide()
  reject()
}

const suggest = async () => {
  const { list, target } = state
  const { value } = target
  const { ownerDocument } = list
  const radios = await search(value)
  const { length } = radios

  if (!length) {
    hide()

    return
  }

  for (const radio of radios) {
    const label = closest('label', radio)
    /*
    const suggestion = segments.join('')
    console.log(suggestion)
    const [first, sep = '', rest = ''] = segments
    const email = suggestion.slice(suggestion.lastIndexOf('<') + 1).replace('>', '')
    const bold = element(list, 'b', {}, sep)
    const radio = element(list, 'input', { type })
    const badge = email.includes('@') ? '' : element(list, 'span', { class: 'ea_generics__badge' }, email)
    const text = element(list, 'span', { class: elements.itemText + ' ' + utilities.elements.ellipsis }, first, bold, rest)
    const content = element(list, 'span', { class: elements.itemContent }, text, badge)
    const label = element(list, 'label', {}, radio, content)
    const li = element(list, 'li', { class: elements.item }, label)
    */
    onBlur.listen(radio)
    onChange.listen(radio)
    onKeyDown.listen(radio)
    onMouseUp.listen(label)
    onMouseOver.listen(label)
    onMouseOut.listen(label)
    onTouchEnd.listen(label)
  }

  const fields = array([target, ...radios])
  const begin = 0
  const index = 0

  update({ begin, index, fields, radios })
  onScroll.listen(ownerDocument)
  position()
}

const position = () => {
  const { completion, list, target } = state
  const { style } = list
  const { width } = rect(completion)

  style.setProperty('--ea_generics_calculated_width', `${width}px`)
  fixed.resize(list, completion)
  fixed.move(list, fixed.fromNode(list, target))
}

const move = (
  key,
  focus = true
) => {
  const { fields, length, list } = state
  const limit = length + 1
  const index = (key + limit) % limit
  const field = fields.at(index)

  if (!index) {
    state.skip += 1
  }

  state.index = index

  one(`.${current}`, list)?.classList.remove(current)
  field.classList.add(current)

  if (focus) {
    field.focus()
  }
}

const resolve = (
  radio
) => {
  const { resolve, target } = state
  const label = closest('label', radio)
  const { textContent } = label

  target.value = radio.value
  hide()
  resolve(textContent)
  update(state)
  target.blur()
  target.focus()
}

const search = async (
  search
) => {
  const { list, target, template } = state
  const { dataset } = template
  const { url } = dataset
  const lightboxes = closest(`.${utilities.elements.aside_lightboxes}`, list)

  list.classList.add(modifiers.list.open)
  if (!lightboxes) {
    sharedClasses.add(list, utilities.modifiers.overflow.hidden)
  }

  if (url) {
    await fetch(search)
  } else {
    await render(template)
  }

  const radios = all('[type="radio"]', list)
  const { length } = radios
  const fields = array([target, ...radios])
  const index = 0

  if (length) {
    sharedClasses.add(list, utilities.modifiers.overflow.hidden)
  }

  update({ fields, index, length })

  return radios
}

const fetch = async (
  search
) => {
  const { template } = state
  const { ownerDocument } = template
  const { location } = ownerDocument
  const { href = location } = one('base', ownerDocument) ?? {}
  const clone = template.cloneNode(true)
  const { dataset } = clone
  const { url } = dataset
  const rewritten = new URL(url, href)
  const { searchParams } = rewritten

  searchParams.set('search', search)
  dataset.url = `${rewritten}`
  await render(clone)
}

const init = () => {
  const [promise, { reject, resolve }] = resolvable()

  state = { promise, reject, resolve }
}

const update = properties => {
  state = Object.assign(state, properties)
}

export default (
  list
) => {
  lists.add(list)
}

export const completed = async () => {
  const { promise } = state

  try {
    await promise
  } catch {}
}

export const completion = (
  target,
  event = null
) => {
  const completion = closest('[data-fragment~="completion"]', target)
  const template = one('template', completion)
  const list = one('[data-contract~="completion.list"]', completion)
  const fields = array([target])
  const begin = -1
  const index = 0
  const length = 0
  const skip = event ? 0 : 1
  const radios = array()
  const { parentNode: wrapper } = target

  init()
  onBlur.listen(target)
  onInput.listen(target)
  onKeyDown.listen(target)
  onMouseUp.listen(target)

  update({
    begin,
    completion,
    fields,
    index,
    length,
    list,
    radios,
    skip,
    target,
    template,
    wrapper
  })

  queueMicrotask(async () => {
    const { children } = list
    const records = mutations(list, options, removed(list))

    queueMicrotask(async () => {
      lists.delete(list)
    })

    for await (const record of records) {
      const { length } = children
      const value = length ? 'true' : 'false'

      if (target.getAttribute('aria-expanded') !== value) {
        target.setAttribute('aria-expanded', value)
      }
    }
  })

  return completed()
}
